/**
 * Displays and polls the status of a Pesapal Cloud POS Push to Pay transaction.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { CheckCircle, CreditCard, Loader2, XCircle } from "lucide-react";
import { api } from "@/store/useStore";
import { toast } from "sonner";

export function PesapalTerminalDialog({ open, invoice, channel = "card", formatCurrency, onClose, onPaid }) {
  const isMpesa = channel === "mpesa";
  const initialMessage = isMpesa ? "STK push sent — customer enters their M-Pesa PIN." : "Present the card on the terminal.";
  const [state, setState] = useState("polling");
  const [message, setMessage] = useState(initialMessage);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const polling = useRef(false);
  const invoiceId = invoice?.id;

  const poll = useCallback(async () => {
    if (!invoiceId || polling.current) return;
    polling.current = true;
    try {
      const response = await api.get(`/invoices/${invoiceId}/pesapal-pos-status`);
      if (response?.status === "paid") {
        setState("paid");
        setMessage("Payment approved.");
        setTimeout(() => onPaid?.(response.receipt_lines || []), 900);
      } else if (response?.status === "failed") {
        setState("failed");
        setMessage(response.message || "Payment was declined.");
      } else if (response?.status === "none") {
        setMessage("No payment is in progress.");
      } else {
        setMessage(response?.message || (isMpesa ? "Waiting for M-Pesa…" : "Waiting for card…"));
      }
    } catch {
      // Keep polling while the terminal finalizes the transaction.
    } finally {
      polling.current = false;
    }
  }, [invoiceId, isMpesa, onPaid]);

  useEffect(() => {
    if (!open) return undefined;
    setState("polling");
    setMessage(initialMessage);
    poll();
    timer.current = setInterval(() => {
      if (state === "polling") poll();
    }, 3000);
    return () => clearInterval(timer.current);
  }, [open, poll, initialMessage, state]);

  useEffect(() => {
    if (state !== "polling" && timer.current) clearInterval(timer.current);
  }, [state]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoiceId}/pesapal-pos-cancel`);
      toast.info("Card transaction cancelled");
    } catch {
      // Closing the dialog is still safe if the cancellation request fails.
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        if (state === "polling") cancel(); else onClose?.();
      }
    }}>
      <DialogContent className="sm:max-w-md" data-testid="pesapal-terminal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className={`h-5 w-5 ${isMpesa ? "text-green-600" : "text-blue-600"}`} />{isMpesa ? "Pesapal M-Pesa" : "Pesapal Card Terminal"}</DialogTitle>
          <DialogDescription>{isMpesa ? "Pesapal · M-Pesa STK push" : "Pesapal Push to Pay · Terminal payment"}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-6">
          {state === "polling" ? <Loader2 className="h-12 w-12 animate-spin text-blue-600" /> : state === "paid" ? <CheckCircle className="h-14 w-14 text-green-600" /> : <XCircle className="h-14 w-14 text-red-600" />}
          {invoice?.total != null && <div className="text-2xl font-extrabold text-blue-600">{formatCurrency ? formatCurrency(invoice.total) : invoice.total}</div>}
          <p className="px-4 text-center text-sm text-muted-foreground">{message}</p>
        </div>
        <div className="flex gap-2">
          {state === "polling" ? <><Button variant="outline" className="flex-1" onClick={poll}>Check now</Button><Button variant="destructive" className="flex-1" onClick={cancel} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel"}</Button></> : state === "paid" ? <Button className="w-full" onClick={() => onPaid?.()}>Done</Button> : <><Button variant="outline" className="flex-1" onClick={poll}>Try again</Button><Button className="flex-1" onClick={onClose}>Close</Button></>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
